import React, { useCallback, useEffect, useState } from 'react';
import parties from '../data/party';

interface Party {
    id: number;
    name: string;
    total_amount?: number | string;
}

const WITHDRAWAL_TYPE_OPTIONS = [
    { value: 'self', label: 'Self Withdraw' },
    { value: 'month_end', label: 'Month End Remaining Amount' },
];

const WithdrawlPage = () => {
    const [partiesList, setPartiesList] = useState<Party[]>([]);
    const [totalamountspend, setTotalAmmountSpend] = useState<number>(0);
    const [withdrawal, setWithdrawal] = useState<{
        partyId: string;
        type: string;
        amount: string;
        date: string;
    }>({
        partyId: '',
        type: '',
        amount: '',
        date: '',
    });
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);
    const [remainingAmount, setRemainingAmount] = useState<number>(0);

    // Fetch parties
    const fetchParties = useCallback(async () => {
        const res = await parties();
        const cleaned = Array.isArray(res)
            ? res.map((item: any) => ({
                id: item.id,
                name: item.name,
                total_amount: item.total_amount !== undefined ? Number(item.total_amount) : undefined,
            }))
            : [];
        setPartiesList(cleaned);
    }, []);

    // Fetch per-party remaining balance
    const fetchUserRemainingBalance = useCallback(async (partyId: string) => {
        if (!partyId) {
            setTotalAmmountSpend(0);
            setRemainingAmount(0);
            return;
        }
        try {
            const usermetaResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta`);
            const usermetaList = await usermetaResponse.json();
            const foundMeta = usermetaList.find(
                (meta: any) => meta.meta_key === "remaining" && String(meta.party_id) === String(partyId)
            );
            if (foundMeta) {
                setTotalAmmountSpend(Number(foundMeta.meta_value));
                setRemainingAmount(Number(foundMeta.meta_value));
            } else {
                const party = partiesList.find(p => String(p.id) === String(partyId));
                const val = Number(party?.total_amount || 0);
                setTotalAmmountSpend(val);
                setRemainingAmount(val);
            }
        } catch (err) {
            setTotalAmmountSpend(0);
            setRemainingAmount(0);
        }
    }, [partiesList]);

    useEffect(() => {
        fetchParties();
    }, [fetchParties]);

    useEffect(() => {
        if (withdrawal.partyId) {
            fetchUserRemainingBalance(withdrawal.partyId);
        } else {
            setRemainingAmount(0);
            setTotalAmmountSpend(0);
        }
    }, [withdrawal.partyId, fetchUserRemainingBalance]);

    const handleWithdrawalInput = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setWithdrawal(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const resetWithdrawalForm = () => {
        setWithdrawal({
            partyId: '',
            type: '',
            amount: '',
            date: '',
        });
        setRemainingAmount(0);
    };

    const handleWithdrawalSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (!withdrawal.partyId) {
            alert("Please select a party for withdrawal.");
            return;
        }
        if (!withdrawal.type) {
            alert("Please select withdrawal type.");
            return;
        }
        if (!withdrawal.date) {
            alert("Please select a date.");
            return;
        }
        const amountValue = parseFloat(withdrawal.amount);
        if (!withdrawal.amount || isNaN(amountValue) || amountValue <= 0) {
            alert("Please enter a valid withdrawal amount.");
            return;
        }
        // if (amountValue > remainingAmount) {
        //     alert(
        //         `Cannot withdraw more than party remaining amount. Remaining: ${remainingAmount}, Requested: ${withdrawal.amount}`
        //     );
        //     return;
        // }

        setTotalAmmountSpend(amountValue);
       

        const payload = {
            party_id: withdrawal.partyId,
            categories: withdrawal.type,
            amount: withdrawal.amount,
            date: withdrawal.date,
        };

        setWithdrawalLoading(true);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/auth/addwithdrawal`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            const result = await res.json();
            if (!res.ok) {
                throw new Error(result?.message || "Failed to add withdrawal");
            }
           
            alert("Withdrawal added successfully!");
            resetWithdrawalForm();
            window.location.reload();
        } catch (err: any) {
            alert("Error: " + (err?.message ?? "Something went wrong"));
        } finally {
            setWithdrawalLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-tr from-slate-100 to-white flex flex-col items-center py-8 px-2">
            <div className="w-full max-w-xl mx-auto flex flex-col items-center">
                <div className="mb-6 w-full flex">
                    <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mx-auto">Withdraw Stock</h1>
                </div>
                <div className="w-full bg-white/90 rounded-2xl shadow-2xl p-8 md:p-10 border border-gray-100">
                    <div className="mb-6 flex flex-col gap-1 items-center">
                        <h2 className="text-xl md:text-2xl font-semibold text-[#1CAC70] tracking-tighter">Withdrawal Form</h2>
                        <span className="text-[#667085] text-sm">Fill in details to withdraw stock</span>
                    </div>
                    <form onSubmit={handleWithdrawalSubmit} autoComplete="off" className="space-y-5">
                        {/* Party Name */}
                        <div>
                            <label htmlFor="withdrawal-partyId" className="block text-sm font-medium mb-1 text-[#374151]">Party Name</label>
                            <div className="relative">
                                <select
                                    name="partyId"
                                    id="withdrawal-partyId"
                                    value={withdrawal.partyId}
                                    onChange={handleWithdrawalInput}
                                    required
                                    className="w-full bg-white border border-gray-300 focus:border-[#1CAC70] focus:ring-2 focus:ring-[#1CAC70]/20 text-gray-800 text-base rounded-lg px-4 py-2 shadow-sm outline-none transition-all"
                                >
                                    <option value="">Choose Party</option>
                                    {partiesList.map((p) => (
                                        <option key={p.id} value={String(p.id)}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {/* Withdrawal Type */}
                        <div>
                            <label htmlFor="withdrawal-type" className="block text-sm font-medium mb-1 text-[#374151]">Withdrawal Type</label>
                            <div className="relative">
                                <select
                                    name="type"
                                    id="withdrawal-type"
                                    value={withdrawal.type}
                                    onChange={handleWithdrawalInput}
                                    required
                                    className="w-full bg-white border border-gray-300 focus:border-[#FF9500] focus:ring-2 focus:ring-[#FF9500]/20 text-gray-800 text-base rounded-lg px-4 py-2 shadow-sm outline-none transition-all"
                                    disabled={!withdrawal.partyId}
                                >
                                    <option value="">Select Option</option>
                                    {WITHDRAWAL_TYPE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {/* Withdrawal Amount */}
                        <div>
                            <label htmlFor="withdrawal-amount" className="block text-sm font-medium mb-1 text-[#374151]">
                                Withdrawal Amount
                                {(withdrawal.partyId && (
                                    <span className="ml-2 text-xs font-mono text-green-700 bg-green-50 px-2 py-1 rounded">
                                        Remaining: {remainingAmount}
                                    </span>
                                ))}
                            </label>
                            <input
                                type="number"
                                name="amount"
                                id="withdrawal-amount"
                                placeholder="Enter amount"
                                value={withdrawal.amount}
                                min={0}
                                step="0.01"
                                onChange={handleWithdrawalInput}
                                required
                                className="w-full bg-gray-50 border border-gray-300 focus:border-[#FF9500] focus:ring-2 focus:ring-[#FF9500]/20 text-gray-800 text-base rounded-lg px-4 py-2 outline-none shadow-sm transition-all disabled:bg-gray-100"
                                disabled={!withdrawal.type}
                            />
                        </div>
                        {/* Date */}
                        <div>
                            <label htmlFor="withdrawal-date" className="block text-sm font-medium mb-1 text-[#374151]">Date</label>
                            <input
                                type="date"
                                name="date"
                                id="withdrawal-date"
                                value={withdrawal.date}
                                onChange={handleWithdrawalInput}
                                required
                                className="w-full bg-gray-50 border border-gray-300 focus:border-[#1CAC70] focus:ring-2 focus:ring-[#1CAC70]/20 text-gray-800 text-base rounded-lg px-4 py-2 outline-none shadow-sm transition-all disabled:bg-gray-100"
                                disabled={!withdrawal.type}
                            />
                        </div>
                        {/* Actions */}
                        <div className="flex flex-col md:flex-row gap-3 pt-3">
                            <button
                                type="submit"
                                disabled={withdrawalLoading}
                                className={`w-full md:w-auto px-6 py-2.5 rounded-lg font-bold text-white shadow 
                                    bg-gradient-to-r from-[#1CAC70] to-[#FF9500] 
                                    hover:from-[#139650] hover:to-[#e28801]
                                    active:scale-95 transition-all outline-none
                                    flex items-center justify-center
                                    ${withdrawalLoading ? 'opacity-70 cursor-not-allowed' : ''}`
                                }
                            >
                                {withdrawalLoading ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                            <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                            <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.536-3.536A9.984 9.984 0 002 12h2z" />
                                        </svg>
                                        Saving...
                                    </span>
                                ) : (
                                    "Submit Withdrawal"
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={resetWithdrawalForm}
                                className="w-full md:w-auto px-6 py-2.5 rounded-lg font-semibold text-gray-600 border border-gray-200 bg-gray-50 hover:bg-gray-100 active:scale-95 shadow transition disabled:opacity-60"
                            >
                                Reset
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default WithdrawlPage;
